import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { ChevronUp } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { type CookiePreferences, useCookieConsent } from '@/hooks/useCookieConsent'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { ROUTES } from '@/navigation/constants'

const TIPI_COOKIE_IMG = '/cookie-consent/tipi-cookie.webp'
export const COOKIE_DECISION_TOAST_MS = 4000

type CookieToggleSwitchProps = {
    checked: boolean
    onToggle?: () => void
    ariaLabel: string
    locked?: boolean
}

const CookieToggleSwitch = ({ checked, onToggle, ariaLabel, locked = false }: CookieToggleSwitchProps) => (
    <button
        type='button'
        role='switch'
        aria-checked={checked}
        aria-label={ariaLabel}
        disabled={locked}
        onClick={locked ? undefined : onToggle}
        className={cn(
            'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
            'focus-visible:shadow-cookie-focus focus-visible:outline-none',
            locked ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
    >
        <span
            aria-hidden
            className={cn(
                'flex h-7 w-[46px] items-center rounded-full border transition-[background] duration-cookie-fast ease-cookie-out',
                checked && !locked && 'border-transparent bg-cookie-flame',
                checked && locked && 'border-transparent bg-cookie-flame-dim opacity-60',
                !checked && 'border-cookie-border bg-cookie-surface',
            )}
        >
            <span
                className={cn(
                    'block size-5 rounded-full transition-[transform,background] duration-cookie ease-cookie-morph',
                    checked ? 'translate-x-[22px] bg-cookie-cream' : 'translate-x-1 bg-cookie-muted',
                )}
            />
        </span>
    </button>
)

type CategoryKey = 'measurement' | 'marketing'

export const CookieConsent = () => {
    const { t } = useTranslation()
    const {
        preferences,
        isVisible,
        isPreferencesPanelOpen,
        acceptAll,
        rejectAll,
        savePreferences,
        closePreferencesPanel,
    } = useCookieConsent()

    const [expanded, setExpanded] = useState(false)
    const [localPreferences, setLocalPreferences] = useState<Pick<CookiePreferences, CategoryKey>>({
        measurement: preferences.measurement,
        marketing: preferences.marketing,
    })
    const [decisionText, setDecisionText] = useState<string | null>(null)

    const showBanner = isVisible || isPreferencesPanelOpen

    useEffect(() => {
        if (isPreferencesPanelOpen) {
            setExpanded(true)
            setLocalPreferences({
                measurement: preferences.measurement,
                marketing: preferences.marketing,
            })
        }
    }, [isPreferencesPanelOpen, preferences.measurement, preferences.marketing])

    useEffect(() => {
        if (!isPreferencesPanelOpen || isVisible) return
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return
            setExpanded(false)
            setLocalPreferences({
                measurement: preferences.measurement,
                marketing: preferences.marketing,
            })
            closePreferencesPanel()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [closePreferencesPanel, isPreferencesPanelOpen, isVisible, preferences.marketing, preferences.measurement])

    useEffect(() => {
        if (!decisionText) return
        const timer = window.setTimeout(() => setDecisionText(null), COOKIE_DECISION_TOAST_MS)
        return () => window.clearTimeout(timer)
    }, [decisionText])

    const handleToggle = (key: CategoryKey) => {
        setLocalPreferences(prev => ({
            ...prev,
            [key]: !prev[key],
        }))
    }

    const finishWithToast = (text: string) => {
        setExpanded(false)
        setDecisionText(text)
        closePreferencesPanel()
    }

    const handleAcceptAll = () => {
        acceptAll()
        finishWithToast(t('cookieConsent.toastAccept'))
    }

    const handleRejectAll = () => {
        rejectAll()
        finishWithToast(t('cookieConsent.toastReject'))
    }

    const handleSave = () => {
        savePreferences({
            functionality: true,
            measurement: localPreferences.measurement,
            marketing: localPreferences.marketing,
        })
        finishWithToast(t('cookieConsent.toastSave'))
    }

    if (!showBanner && !decisionText) return null
    if (typeof document === 'undefined') return null

    return createPortal(
        <div className='pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-stretch justify-end'>
            {showBanner ? (
                <div
                    role='dialog'
                    aria-label={t('cookieConsent.title')}
                    className='animate-tipi-rise pointer-events-auto relative w-full font-archivo text-cookie-fg'
                >
                    <img
                        src={TIPI_COOKIE_IMG}
                        alt={t('cookieConsent.mascotAlt')}
                        width={132}
                        height={197}
                        className='pointer-events-none absolute left-1/2 top-[-72px] z-[2] w-20 -translate-x-1/2 md:left-3 md:top-[-88px] md:w-[108px] md:translate-x-0'
                        style={{ filter: 'drop-shadow(0 14px 26px rgba(18, 12, 9, 0.65))' }}
                    />

                    <div className='relative max-h-[min(85dvh,720px)] overflow-y-auto overscroll-contain rounded-t-cookie-lg border border-b-0 border-cookie-border bg-cookie-shell shadow-cookie'>
                        <div className='flex w-full flex-col gap-3 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 md:gap-3 md:px-5 md:pb-[max(0.875rem,env(safe-area-inset-bottom))] md:pt-3.5'>
                            <div className='flex flex-col items-stretch gap-3 pt-12 md:flex-row md:items-center md:gap-5 md:pl-[112px] md:pt-0 lg:gap-6'>
                                <div className='flex min-w-0 flex-1 flex-col items-center gap-1 text-center md:items-start md:text-left'>
                                    <h2 className='text-base font-bold leading-[1.25] tracking-[-0.02em] text-cookie-fg md:text-[17px]'>
                                        {t('cookieConsent.title')}
                                    </h2>
                                    <p className='text-pretty text-[13px] leading-snug text-cookie-muted md:max-w-xl md:text-sm md:leading-normal'>
                                        {t('cookieConsent.body')}
                                    </p>
                                </div>

                                <div className='grid w-full shrink-0 grid-cols-2 gap-2 md:w-auto md:min-w-[280px] md:gap-2.5'>
                                    <Button
                                        type='button'
                                        variant='outline'
                                        onClick={handleRejectAll}
                                        className='h-11 min-h-11 rounded-full border-cookie-border bg-transparent px-2 text-[13px] font-bold tracking-[0.01em] text-cookie-fg hover:bg-cookie-surface hover:text-cookie-fg focus-visible:shadow-cookie-focus focus-visible:ring-0 focus-visible:ring-offset-0 md:h-12 md:min-h-12 md:min-w-[132px] md:px-5 md:text-[15px]'
                                    >
                                        {t('cookieConsent.rejectAll')}
                                    </Button>
                                    <Button
                                        type='button'
                                        onClick={handleAcceptAll}
                                        className='h-11 min-h-11 rounded-full bg-cookie-flame px-2 text-[13px] font-bold tracking-[0.01em] text-cookie-fg hover:bg-cookie-flame-strong focus-visible:shadow-cookie-focus focus-visible:ring-0 focus-visible:ring-offset-0 md:h-12 md:min-h-12 md:min-w-[132px] md:px-5 md:text-[15px]'
                                    >
                                        {t('cookieConsent.acceptAll')}
                                    </Button>
                                </div>

                                <div className='flex shrink-0 flex-wrap items-center justify-center gap-x-4 gap-y-1 md:flex-col md:items-start md:gap-1 lg:flex-row lg:items-center lg:gap-x-4'>
                                    <button
                                        type='button'
                                        onClick={() => setExpanded(prev => !prev)}
                                        className='inline-flex items-center gap-1.5 px-1 py-1 text-[13px] font-extrabold text-cookie-muted transition-colors hover:text-cookie-fg focus-visible:rounded-md focus-visible:shadow-cookie-focus focus-visible:outline-none'
                                    >
                                        <ChevronUp
                                            className={cn(
                                                'size-3.5 transition-transform duration-cookie ease-cookie-morph',
                                                expanded && 'rotate-180',
                                            )}
                                            strokeWidth={3.5}
                                            aria-hidden
                                        />
                                        <span>{t('cookieConsent.customize')}</span>
                                    </button>
                                    <LocaleLink
                                        to={ROUTES.cookiePolicy}
                                        className='text-xs font-medium text-cookie-muted no-underline transition-colors hover:text-cookie-flame focus-visible:rounded-md focus-visible:shadow-cookie-focus focus-visible:outline-none'
                                    >
                                        {t('cookieConsent.cookiePolicy')}
                                    </LocaleLink>
                                </div>
                            </div>

                            {expanded ? (
                                <div className='flex flex-col gap-0.5 border-t border-cookie-border pt-2 md:pl-[112px]'>
                                    <div className='grid gap-0 md:grid-cols-3 md:gap-4'>
                                        <div className='flex items-start gap-3 py-2 md:py-2.5'>
                                            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                                                <span className='text-sm font-bold tracking-[-0.01em] text-cookie-fg'>
                                                    {t('cookieConsent.necessary')}
                                                </span>
                                                <span className='text-[11px] leading-[1.45] text-cookie-muted'>
                                                    {t('cookieConsent.necessaryDescription')}
                                                </span>
                                            </div>
                                            <CookieToggleSwitch
                                                checked
                                                locked
                                                ariaLabel={t('cookieConsent.necessary')}
                                            />
                                        </div>

                                        <div className='flex items-start gap-3 py-2 md:py-2.5'>
                                            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                                                <span className='text-sm font-bold tracking-[-0.01em] text-cookie-fg'>
                                                    {t('cookieConsent.analytics')}
                                                </span>
                                                <span className='text-[11px] leading-[1.45] text-cookie-muted'>
                                                    {t('cookieConsent.analyticsDescription')}
                                                </span>
                                            </div>
                                            <CookieToggleSwitch
                                                checked={localPreferences.measurement}
                                                onToggle={() => handleToggle('measurement')}
                                                ariaLabel={t('cookieConsent.analytics')}
                                            />
                                        </div>

                                        <div className='flex items-start gap-3 py-2 md:py-2.5'>
                                            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                                                <span className='text-sm font-bold tracking-[-0.01em] text-cookie-fg'>
                                                    {t('cookieConsent.marketing')}
                                                </span>
                                                <span className='text-[11px] leading-[1.45] text-cookie-muted'>
                                                    {t('cookieConsent.marketingDescription')}
                                                </span>
                                            </div>
                                            <CookieToggleSwitch
                                                checked={localPreferences.marketing}
                                                onToggle={() => handleToggle('marketing')}
                                                ariaLabel={t('cookieConsent.marketing')}
                                            />
                                        </div>
                                    </div>

                                    <div className='flex justify-stretch pt-1 md:justify-end'>
                                        <Button
                                            type='button'
                                            onClick={handleSave}
                                            className='hover:bg-cookie-cream/90 h-10 min-h-10 w-full rounded-full bg-cookie-cream px-4 text-[13px] font-bold tracking-[0.01em] text-cookie-cream-fg focus-visible:shadow-cookie-focus focus-visible:ring-0 focus-visible:ring-offset-0 md:w-auto'
                                        >
                                            {t('cookieConsent.savePreferences')}
                                        </Button>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}

            {decisionText ? (
                <div className='pointer-events-none flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3'>
                    <div className='animate-tipi-rise-fast pointer-events-auto flex max-w-[min(100%,560px)] items-center gap-3 rounded-full border border-cookie-border bg-cookie-surface py-2.5 pl-3 pr-4 font-archivo shadow-cookie-card'>
                        <img src={TIPI_COOKIE_IMG} alt='' width={34} height={51} className='w-[34px] shrink-0' />
                        <p className='text-pretty text-[13px] font-semibold text-cookie-fg'>{decisionText}</p>
                    </div>
                </div>
            ) : null}
        </div>,
        document.body,
    )
}
